import { NextPage } from 'next'
import Head from 'next/head'
import Link from 'next/link'
import { useTranslation } from 'react-i18next'
import useSWR from 'swr'
import { useAccount } from 'wagmi'
import {
  getAddressShare,
  getGenesisReward,
  getLonBalance,
  getLonPoolOverview,
} from '~/apis'
import IconArrowRightLight from '~/assets/images/arrow-right-light.svg'
import IconFavicon from '~/assets/images/favicon.png'
import IconLogoBuyback from '~/assets/images/buyback-light.svg'
import IconLpMiningLight from '~/assets/images/lp-mining-light.png'
import IconQuestionMark from '~/assets/images/question-mark.svg'
import IconStakeMiningLight from '~/assets/images/stake-mining-light.svg'
import IconTradeMiningLight from '~/assets/images/trade-mining-light.svg'
import Footer from '~/components/Footer'
import Header from '~/components/Header'
import { LOCALES } from '~/locales'
import { useModalToggle } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import { formatNumber, formatPercentage } from '~/utils/numbers'
import {
  StyledLonDashboard,
  StyledLonPageHelper,
  StyledBadge,
  StyledLink,
} from './styled'
import { useGA, useUpdaterChainId } from '~/hooks'

const Dashboard: NextPage = () => {
  const { i18n, t } = useTranslation()
  const isZh = i18n.resolvedLanguage === LOCALES.ZH
  const { address: account } = useAccount()
  const chainId = useUpdaterChainId()

  const toggleWalletModal = useModalToggle(ApplicationModal.WALLET_CONNECT, {
    modalState: { from: 'dashboard' },
  })
  const { trackElClickCurring, trackElClick } = useGA()

  const { data: lonBalance } = useSWR(['getLonBalance', chainId, account], () =>
    getLonBalance(chainId, account),
  )
  const { data: overview } = useSWR('getLonPoolOverview', getLonPoolOverview)
  const { data: addressAccount } = useSWR(['getAddressShare', account], () =>
    getAddressShare(account),
  )
  const { data: genesisRewards } = useSWR(['getGenesisReward', account], () =>
    getGenesisReward(account),
  )
  const feeFactor = addressAccount?.feeFactor
  const genesisReward = genesisRewards?.find((t) => t.period === 0)

  return (
    <StyledLonPageHelper>
      <Head>
        <title>{t('meta_lon_dashboard_title')}</title>
        <meta
          name="description"
          content={t('meta_lon_dashboard_description')}
        />
        <meta name="keywords" content={t('meta_lon_keywords')} />
      </Head>
      <Header />
      <StyledLonDashboard>
        <div className="lon-dashboard-inner">
          <div className="logo-wrap">
            <div>
              <img src={IconFavicon.src} />
            </div>
            <p className="row name-wrap">
              <span className="title">LON</span>
              <span style={{ color: '#9aa3ae', margin: '0 10px' }}>|</span>
              <span className="subtitle">Tokenlon Utility Token</span>
            </p>
          </div>
          <div className="overview">
            <div className="overall">
              <div className="lon-cell volume-24h">
                <div>
                  <dd>{t('tokenlon_24h_vol')}</dd>
                  <dt>{formatNumber(overview?.volume24h, 2)}</dt>
                </div>
              </div>

              <div className="lon-cell">
                <div>
                  <dd>{t('trades')}</dd>
                  <dt>{formatNumber(overview?.trades)}</dt>
                </div>
              </div>
              <div className="lon-cell">
                <div>
                  <dd>{t('wallet_amount')}</dd>
                  <dt>{formatNumber(overview?.uniqTraders)}</dt>
                </div>
              </div>

              <div className="lon-cell">
                <div>
                  <dd>{t('published')}</dd>
                  <dt>{formatNumber(overview?.supply, 2, 18)}</dt>
                </div>
              </div>

              <div className="lon-cell">
                <div>
                  <dd>{t('circulation')}</dd>
                  <dt>{formatNumber(overview?.liquidity, 2)}</dt>
                </div>
              </div>

              <div className="lon-cell">
                <div>
                  <dd>{t('holders')}</dd>
                  <dt>{formatNumber(overview?.holders)}</dt>
                </div>
              </div>
            </div>

            <p className="divide-title">{t('my_data')}</p>

            {account ? (
              <div className="mine">
                <div className="lon-cell">
                  <div>
                    <dd>{t('lon_balance')}</dd>
                    <dt>{formatNumber(lonBalance, 6)}</dt>
                  </div>
                  <div>
                    <dd style={{ textAlign: 'right' }}>{t('tokenlon_fee')}</dd>
                    <dt style={{ textAlign: 'right' }}>
                      {feeFactor && formatPercentage(feeFactor / 10000, 2)}
                    </dt>
                  </div>
                </div>
                {genesisReward && (
                  <div className="lon-cell red">
                    <div>
                      <dd>
                        <span>{t('lon_to_be_claimed')}</span>
                        <a
                          href={`https://tokenlon.zendesk.com/hc/${
                            isZh ? 'zh-cn' : 'en-us'
                          }/articles/360053731671`}
                          style={{ marginLeft: 4 }}
                        >
                          <img src={IconQuestionMark} />
                        </a>
                      </dd>
                      <dt>{formatNumber(genesisReward?.balance, 2, 18)}</dt>
                    </div>
                    <div>
                      <Link href="/lon/mining/trade">
                        <button
                          className="primary"
                          data-trackid="webtl_dashboard_trademining"
                          data-trackdata={JSON.stringify({
                            url: '/lon/mining/trade',
                          })}
                          onClick={trackElClick}
                        >
                          {t('detail')}
                        </button>
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <div className="mine">
                <div className="lon-cell">
                  <div>
                    <dd>{t('lon_balance')}</dd>
                    <dt
                      className="tip"
                      data-trackid="webtl_walletconnect"
                      data-trackdata={JSON.stringify({ from: 'dashboard' })}
                      onClick={(e) => trackElClickCurring(e)(toggleWalletModal)}
                    >
                      {t('check_after_connect_wallet')}
                    </dt>
                  </div>
                </div>
              </div>
            )}

            <div className="mining">
              <div className="mining-cards">
                <StyledLink href="/lon/mining/trade">
                  <div
                    className="lon-cell mining-card"
                    data-trackid="webtl_dashboard_trademining"
                    data-trackdata={JSON.stringify({
                      url: '/lon/mining/trade',
                    })}
                    onClick={trackElClick}
                  >
                    <div>
                      <img src={IconTradeMiningLight} className="m-icon" />
                      <p>{t('sub_nav_trade_mining')}</p>
                    </div>
                    <img src={IconArrowRightLight} />
                  </div>
                </StyledLink>
                <StyledLink href="/lon/buyback">
                  <div
                    className="lon-cell mining-card"
                    data-trackid="webtl_dashboard_buyback"
                    data-trackdata={JSON.stringify({ url: '/lon/buyback' })}
                    onClick={trackElClick}
                  >
                    <div>
                      <img src={IconLogoBuyback} className="m-icon" />
                      <div>
                        <p>{t('sub_nav_buyback')}</p>
                        <StyledBadge>New</StyledBadge>
                      </div>
                    </div>
                    <img src={IconArrowRightLight} />
                  </div>
                </StyledLink>
                <StyledLink href="/lon/staking">
                  <div
                    className="lon-cell mining-card"
                    data-trackid="webtl_dashboard_staking"
                    data-trackdata={JSON.stringify({ url: '/lon/staking' })}
                    onClick={trackElClick}
                  >
                    <div>
                      <img src={IconStakeMiningLight} className="m-icon" />
                      <p>{t('sub_nav_staking')}</p>
                    </div>
                    <img src={IconArrowRightLight} />
                  </div>
                </StyledLink>
                <StyledLink href="/lon/mining/lp">
                  <div
                    className="lon-cell mining-card"
                    data-trackid="webtl_dashboard_liquidmining"
                    data-trackdata={JSON.stringify({ url: '/lon/mining/lp' })}
                    onClick={trackElClick}
                  >
                    <div>
                      <img src={IconLpMiningLight.src} className="m-icon" />
                      <div>
                        <p>{t('lp_mining')}</p>
                        <StyledBadge bgColor="#B9BED3">Closed</StyledBadge>
                      </div>
                    </div>
                    <img src={IconArrowRightLight} />
                  </div>
                </StyledLink>
              </div>
            </div>
          </div>
        </div>
      </StyledLonDashboard>
      <Footer />
    </StyledLonPageHelper>
  )
}

export default Dashboard
